import React, { useState, useEffect } from 'react';
import { produkHukumPublicService } from '../../services/produkHukumPublicService';
import {
    FaSearch,
    FaDownload,
    FaCalendarAlt,
    FaFilePdf,
    FaGavel,
    FaFilter,
    FaEye,
    FaTimes,
    FaExclamationTriangle,
    FaSpinner
} from 'react-icons/fa';

// Kategori dengan icon
const kategoriOptions = [
    { id: 'perda', title: 'Peraturan Daerah', icon: '📜' },
    { id: 'perwali', title: 'Peraturan Walikota', icon: '🏛️' },
    { id: 'kepwal', title: 'Keputusan Walikota', icon: '📋' },
    { id: 'kepkadinas', title: 'Keputusan Kepala Dinas', icon: '📄' },
    { id: 'se', title: 'Surat Edaran (SE)', icon: '✉️' },
    { id: 'lainya', title: 'Lainnya', icon: '📁' },
];

const ProdukHukumPage = () => {
    const [activeCategory, setActiveCategory] = useState('perda');
    const [allData, setAllData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewFile, setPreviewFile] = useState({ url: '', title: '' });
    const [iframeError, setIframeError] = useState(false);

    // Load data saat mount
    useEffect(() => {
        loadData();
    }, []);

    // Scroll to top saat kategori berubah
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeCategory]);

    const loadData = async () => {
        setLoading(true);
        setError('');

        const result = await produkHukumPublicService.getProdukHukum();

        if (result.success) {
            setAllData(result.data.data || []);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    // Filter berdasarkan kategori & search
    const getFilteredItems = () => {
        let items = allData.filter(item => item.kategori === activeCategory);

        if (searchTerm) {
            items = items.filter(item =>
                item.judul?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.deskripsi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.nomor?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return items;
    };

    // Hitung jumlah dokumen per kategori
    const getCategoryCount = (catId) => {
        return allData.filter(item => item.kategori === catId).length;
    };

    const filteredItems = getFilteredItems();
    const activeKategori = kategoriOptions.find(k => k.id === activeCategory);

    const handlePreview = (fileUrl, title) => {
        setPreviewFile({ url: fileUrl, title });
        setShowPreview(true);
        setIframeError(false);
    };

    const closePreview = () => {
        setShowPreview(false);
        setPreviewFile({ url: '', title: '' });
        setIframeError(false);
    };

    const handleDownload = (fileUrl, title) => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = title + '.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Berlaku':
                return 'bg-green-100 text-green-700';
            case 'Dicabut':
                return 'bg-red-100 text-red-700';
            case 'Diubah':
                return 'bg-yellow-100 text-yellow-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    // ============ LOADING STATE ============
    if (loading) {
        return (
            <div className="container mx-auto px-4 py-20 text-center">
                <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                <p className="text-gray-500">Memuat produk hukum...</p>
            </div>
        );
    }

    // ============ ERROR STATE ============
    if (error) {
        return (
            <div className="container mx-auto px-4 py-20">
                <div className="card-glass text-center py-12 max-w-2xl mx-auto">
                    <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-700 mb-2">Gagal Memuat Data</h2>
                    <p className="text-gray-500 mb-6">{error}</p>
                    <button onClick={loadData} className="btn-primary">
                        Coba Lagi
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-12">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                    <FaGavel className="text-4xl text-[#2E7D32]" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A]">
                            Produk Hukum
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Kumpulan produk hukum BPKAD dan Pemerintah Kota Magelang
                        </p>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
                <div className="relative">
                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari produk hukum berdasarkan judul, nomor, atau deskripsi..."
                        className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent outline-none"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            {/* Kategori Navigation */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                {kategoriOptions.map((cat) => {
                    const count = getCategoryCount(cat.id);
                    return (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`p-4 rounded-xl text-center transition-all duration-300 ${activeCategory === cat.id
                                    ? 'bg-[#0F4C3A] text-white shadow-lg transform scale-105'
                                    : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                                }`}
                        >
                            <div className="text-3xl mb-2">{cat.icon}</div>
                            <h3 className="font-semibold text-xs leading-tight">{cat.title}</h3>
                            <p className={`text-xs mt-1 ${activeCategory === cat.id ? 'text-white/70' : 'text-gray-400'}`}>
                                {count} Dokumen
                            </p>
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            <div>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">
                        {activeKategori?.title || 'Dokumen'}
                    </h2>
                    <div className="flex items-center text-sm text-gray-500">
                        <FaFilter className="mr-2" />
                        {filteredItems.length} dokumen ditemukan
                    </div>
                </div>

                {filteredItems.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow">
                        <FaSearch className="text-6xl text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-500 mb-2">Tidak Ditemukan</h3>
                        <p className="text-gray-400">
                            {searchTerm
                                ? 'Tidak ada produk hukum yang sesuai dengan pencarian'
                                : 'Belum ada dokumen untuk kategori ini'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredItems.map((item) => (
                            <div
                                key={item.id}
                                className="bg-white p-4 rounded-xl shadow hover:shadow-lg transition-shadow"
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-start mb-3">
                                            <div className="w-10 h-10 bg-[#2E7D32]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                                <FaFilePdf className="text-[#2E7D32] text-xl" />
                                            </div>
                                            <div className="flex-1">
                                                {item.nomor && (
                                                    <span className="text-xs text-gray-400 block mb-1">
                                                        No. {item.nomor}
                                                    </span>
                                                )}
                                                <h3 className="text-lg font-semibold text-[#0F4C3A]">
                                                    {item.judul}
                                                </h3>
                                                {item.deskripsi && (
                                                    <p className="text-gray-600 text-sm mt-1">
                                                        {item.deskripsi}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3 ml-14">
                                            <span className="flex items-center text-sm text-gray-500">
                                                <FaCalendarAlt className="mr-1" />
                                                {formatDate(item.tanggal)}
                                            </span>
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                                                {item.status}
                                            </span>
                                            {item.downloads > 0 && (
                                                <span className="text-xs text-gray-400">
                                                    {item.downloads} downloads
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 md:ml-6 flex-shrink-0">
                                        <button
                                            onClick={() => handlePreview(item.file_url, item.judul)}
                                            className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-semibold px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                                        >
                                            <FaEye className="mr-2" />
                                            Preview
                                        </button>
                                        <button
                                            onClick={() => handleDownload(item.file_url, item.judul)}
                                            className="flex items-center text-sm text-[#2E7D32] hover:text-[#4CAF50] font-semibold px-3 py-2 rounded-lg hover:bg-green-50 transition-colors"
                                        >
                                            <FaDownload className="mr-2" />
                                            Download
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal Preview */}
            {showPreview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-xl font-bold text-[#0F4C3A] truncate pr-4">
                                {previewFile.title}
                            </h3>
                            <button
                                onClick={closePreview}
                                className="text-gray-500 hover:text-gray-700 transition-colors p-1 hover:bg-gray-100 rounded-full flex-shrink-0"
                            >
                                <FaTimes size={24} />
                            </button>
                        </div>

                        {/* PDF Viewer */}
                        <div className="flex-1 p-4 overflow-auto relative">
                            {iframeError ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500 py-20">
                                    <FaExclamationTriangle className="text-6xl text-yellow-500 mb-4" />
                                    <p className="text-lg font-semibold mb-2">Gagal memuat pratinjau</p>
                                    <p className="text-sm text-center max-w-md">
                                        File mungkin tidak dapat ditampilkan di iframe.
                                        Silakan unduh untuk melihat isi dokumen.
                                    </p>
                                    <button
                                        onClick={() => handleDownload(previewFile.url, previewFile.title)}
                                        className="mt-6 btn-primary"
                                    >
                                        <FaDownload className="mr-2 inline" />
                                        Unduh File
                                    </button>
                                </div>
                            ) : (
                                <iframe
                                    src={previewFile.url}
                                    title={previewFile.title}
                                    className="w-full h-[70vh] border-0 rounded-lg"
                                    style={{ minHeight: '400px' }}
                                    onError={() => setIframeError(true)}
                                />
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
                            <button
                                onClick={() => handleDownload(previewFile.url, previewFile.title)}
                                className="flex items-center px-4 py-2 bg-[#2E7D32] text-white rounded-lg hover:bg-[#4CAF50] transition-colors"
                            >
                                <FaDownload className="mr-2" />
                                Unduh File
                            </button>
                            <button
                                onClick={closePreview}
                                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Tutup
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProdukHukumPage;